import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import apiService, { api } from '../services/api';
import { setCurrentUser } from '../utils/permissions';
import { clearSelectedCompany } from '../utils/selectedCompany';
import { clearViewedCompany } from '../utils/resellerCompany';
import { clearSuperAdminScope } from '../utils/superAdminCompanyScope';

interface AdminModule {
  id: number;
  name: string;
  displayName: string;
  description: string;
  route: string;
  icon: string;
  category?: string;
  parentModule?: string;
}

interface AdminPermission {
  id: number;
  moduleId: number;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  module: AdminModule;
}

interface RolePermission {
  id: number;
  moduleId: number;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  module: AdminModule;
}

interface UserRole {
  id: number;
  name: string;
  description: string;
  permissions: RolePermission[];
}

interface User {
  id: number;
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  role: 'SUPERADMIN' | 'ADMIN' | 'EMPLOYEE';
  hasWaterJacket?: boolean;
  hasRapierJacket?: boolean;
  isActive?: boolean;
  createdAt?: string;
  tenantId?: number | null;
  isReseller?: boolean;
  parentResellerId?: number | null;
  machineTenantIds?: number[] | null;
  machineCompanies?: { tenantId: number; name: string }[];
  companyAccess?: { tenantId: number; canWrite: boolean }[] | null;
  companyAccessCompanies?: { tenantId: number; name: string; canWrite: boolean }[];
  employee?: {
    id: number;
    type: 'FIXED' | 'METER';
    rate: number;
    designation?: string | null;
    canApproveAttendance?: boolean;
  };
  permissions?: AdminPermission[];
  userRole?: UserRole;
}

interface AuthContextType {
  user: User | null;
  /** True only while checking stored token on app load — used by route guards. */
  loading: boolean;
  /** True while login/register request is in flight — does not unmount public routes. */
  authActionLoading: boolean;
  /** Accepts an email address or a phone number as the identifier. */
  login: (identifier: string, password: string) => Promise<User>;
  register: (name: string, email: string, password: string, role?: string, employeeType?: string, employeeRate?: number) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authActionLoading, setAuthActionLoading] = useState(false);
  const { t } = useTranslation();

  // Mirror the user into the shared permission store so the api.ts write-guard
  // (which runs outside React) can read the current module permissions.
  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await apiService.get('/auth/profile');
        setUser(response.user);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('token');
        delete api.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (identifier: string, password: string): Promise<User> => {
    try {
      setAuthActionLoading(true);
      console.log('🔐 Attempting login for:', identifier);
      const response = await apiService.post('/auth/login', { identifier, password });
      console.log('✅ Login response:', response);

      const { user: userData, token } = response;
      console.log('👤 User data:', userData);
      console.log('🔑 Token:', token ? 'Present' : 'Missing');

      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);

      toast.success(t('auth.loginSuccess'));
      console.log('🎉 Login successful, user set in context');
      return userData;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      // Don't show toast here - let the component handle the error display
      throw error;
    } finally {
      setAuthActionLoading(false);
    }
  };

  const register = async (
    name: string, 
    email: string, 
    password: string, 
    role = 'EMPLOYEE',
    employeeType?: string,
    employeeRate?: number
  ) => {
    try {
      setAuthActionLoading(true);
      const response = await apiService.post('/auth/register', {
        name,
        email,
        password,
        role,
        employeeType,
        employeeRate
      });
      const { user: userData, token } = response;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      
      toast.success(t('auth.registerSuccess'));
    } catch (error: any) {
      const message = error.response?.data?.message || t('auth.registerError');
      toast.error(message);
      throw error;
    } finally {
      setAuthActionLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    // Drop any machine company selection so it can't leak into the next login.
    clearSelectedCompany();
    clearViewedCompany();
    clearSuperAdminScope();
    setUser(null);
    toast.success(t('common.logout'));
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    authActionLoading,
    login,
    register,
    logout,
    updateUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
